import { Editor as SlateEditor, Point, Element, Path, NodeEntry } from 'slate';
import { TagWithMetadata } from 'entities/tag';
import { CustomEditor } from 'slate/types/slate';
import { getBlockTagBindings } from 'slate/extensions/tag/types/block';

function findTagInEditor(editor: CustomEditor, tagId: string): NodeEntry | null {
  const inlineEntry = Array.from(
    SlateEditor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && n.type === 'tag' && n.tagId === tagId,
    })
  )[0];
  if (inlineEntry) return inlineEntry;

  const blockEntry = Array.from(
    SlateEditor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && getBlockTagBindings(n).some((b) => b.tagId === tagId),
    })
  )[0];
  return blockEntry ?? null;
}

export function buildChainContaining(existingTags: TagWithMetadata[], tagId: string): TagWithMetadata[] {
  const tagMap = new Map(existingTags.map((t) => [t.id, t]));
  let head = tagMap.get(tagId);
  if (!head) return [];

  const visited = new Set<string>([head.id]);
  for (;;) {
    const headId: string = head.id;
    const predecessor: TagWithMetadata | undefined = existingTags.find((t) => t.nextSegment === headId);
    if (!predecessor || visited.has(predecessor.id)) break;
    visited.add(predecessor.id);
    head = predecessor;
  }

  const chain: TagWithMetadata[] = [];
  const seen = new Set<string>();
  let current: TagWithMetadata | undefined = head;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.push(current);
    current = current.nextSegment ? tagMap.get(current.nextSegment) : undefined;
  }
  return chain;
}

export function buildChain(existingTags: TagWithMetadata[]): TagWithMetadata[] {
  if (existingTags.length === 0) return [];
  if (existingTags.length === 1) return existingTags;

  const tagMap = new Map(existingTags.map((t) => [t.id, t]));
  const referencedByNext = new Set(existingTags.map((t) => t.nextSegment).filter((id): id is string => !!id));
  const heads = existingTags.filter((t) => !referencedByNext.has(t.id));

  const chain: TagWithMetadata[] = [];
  let current: TagWithMetadata | undefined = heads[0] || existingTags[0];
  const visited = new Set<string>();

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    chain.push(current);
    if (!current.nextSegment) break;
    current = tagMap.get(current.nextSegment);
  }

  return chain;
}

export function findChainInsertionPoint(
  existingTags: TagWithMetadata[],
  editor: CustomEditor,
  currentSectionSuperId: string,
  documentSections: { superId: string }[],
  anchorPath?: Path
): { predecessor: TagWithMetadata | null; successor: TagWithMetadata | null } {
  const chain = buildChain(existingTags);
  if (chain.length === 0) return { predecessor: null, successor: null };

  let selectionPoint: Point;
  if (anchorPath) {
    selectionPoint = SlateEditor.start(editor, anchorPath);
  } else if (editor.selection) {
    selectionPoint = SlateEditor.start(editor, editor.selection);
  } else {
    return { predecessor: chain[chain.length - 1], successor: null };
  }

  // Section order map for tags not in the current editor
  const sectionOrderMap = new Map<string, number>();
  documentSections.forEach((s, idx) => sectionOrderMap.set(s.superId, idx));
  const currentSectionIndex = sectionOrderMap.get(currentSectionSuperId) ?? Infinity;

  let predecessor: TagWithMetadata | null = null;
  let successor: TagWithMetadata | null = null;

  for (const tag of chain) {
    const entry = findTagInEditor(editor, tag.id);

    if (!entry) {
      if (tag.sectionSuperId === currentSectionSuperId) continue;
      // Tag not in the current editor (different section) — use section ordering
      const tagSectionIndex = (tag.sectionSuperId ? sectionOrderMap.get(tag.sectionSuperId) : undefined) ?? Infinity;
      if (tagSectionIndex <= currentSectionIndex) {
        predecessor = tag;
      } else if (!successor) {
        successor = tag;
      }
      continue;
    }

    // Tag is in the current editor — compare actual document positions
    const [, tagPath] = entry;
    const tagEnd = SlateEditor.end(editor, tagPath);

    if (Point.compare(tagEnd, selectionPoint) <= 0) {
      predecessor = tag;
    } else if (!successor) {
      successor = tag;
    }
  }

  return { predecessor, successor };
}
